import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useV2Auth } from '@/context/V2AuthContext';
import { PrimaryAction, SecondaryAction } from '@/components/mask/MaskSystem';
import '@/components/mask/mask.css';

/* Guide — CarmaCircle 2.0: Mensch → Mensch. In Ruhe kennenlernen, ehrlich zurückmelden. */

const SECTIONS = [
  {
    num: '01',
    title: 'Der erste Eindruck',
    intro: 'Öffne die Startseite, als würdest Du CarmaCircle zum ersten Mal sehen.',
    points: [
      'Verstehst Du ohne Erklärung, worum es hier geht?',
      'Wird klar: Es geht um echte Wertschätzung zwischen Menschen — nicht um ein soziales Netzwerk?',
      'Fühlt sich die Seite ruhig an, oder wirkt etwas laut oder werblich?',
    ],
  },
  {
    num: '02',
    title: 'Geben',
    intro: 'Denk an einen Menschen, dem Du schon lange etwas sagen wolltest — und geh über GEBEN den ganzen Weg.',
    points: [
      'Wähle eine Person aus — oder gib einen Namen ein, der noch nicht hier ist, und verschenke Dein Carma als persönlichen Link.',
      'Schreib in Ruhe. Ein Satz genügt.',
      'Wie fühlt sich der Moment des Aussprechens an? Wirst Du geführt, ohne gedrängt zu werden?',
    ],
  },
  {
    num: '03',
    title: 'Empfangen — auch ohne Account',
    intro: 'Öffne einen Carma-Link in einem privaten Fenster, um ihn so zu erleben wie ein Mensch ohne Account.',
    points: [
      'Kannst Du die Worte lesen, ohne Dich anmelden zu müssen?',
      'Nimm Dir Zeit: Nach dem Lesen verändert sich die Seite leise. Wirkt dieser Übergang stimmig?',
      'Probiere das Antworten, das Weitergeben und das Bewahren am Ende.',
    ],
  },
  {
    num: '04',
    title: 'Für Dich',
    intro: 'FÜR DICH ist kein Posteingang, sondern Dein persönlicher Raum für empfangene Worte.',
    points: [
      'Entdecke wartende Carma — Moment für Moment, eines nach dem anderen.',
      'Nach dem Lesen erscheint ein leiser Impuls. Halte Deine Gedanken dazu fest, wenn Du magst.',
      'Bewahre, was Dir besonders viel bedeutet — und schau, wo es danach seinen Platz findet.',
      'Die klassische Übersicht findest Du unter „Alle ansehen“ — brauchst Du sie, oder trägt der Raum allein?',
    ],
  },
  {
    num: '05',
    title: 'Ich',
    intro: 'ICH ist Dein Raum zum Erinnern und Reflektieren — kein Profil, kein Dashboard.',
    points: [
      'Halte im Tagebuch einen Gedanken fest — nur für Dich.',
      'Sieh Dir unter „Worte, die bleiben.“ Deine bewahrten Carma an.',
      'Konto, Einstellungen und Abmelden findest Du bewusst zurückgenommen ganz unten.',
    ],
  },
];

export default function BetaGuideV2() {
  const { user, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const [whatWorked, setWhatWorked] = useState('');
  const [whatConfused, setWhatConfused] = useState('');
  const [wishes, setWishes] = useState('');
  const [generalFeedback, setGeneralFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const hasContent = whatWorked.trim() || whatConfused.trim() || wishes.trim() || generalFeedback.trim();

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!hasContent) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(
        `${API_V2}/feedback`,
        {
          whatWorked: whatWorked.trim() || null,
          whatConfused: whatConfused.trim() || null,
          wishes: wishes.trim() || null,
          generalFeedback: generalFeedback.trim() || null,
        },
        { withCredentials: true }
      );
      setSubmitted(true);
      setWhatWorked(''); setWhatConfused(''); setWishes(''); setGeneralFeedback('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Feedback konnte nicht gesendet werden.');
    } finally {
      setSubmitting(false);
    }
  };

  const fields = [
    { label: 'Was hat sich gut angefühlt?', value: whatWorked, set: setWhatWorked, ph: 'Ein Moment, der für Dich funktioniert hat …' },
    { label: 'Wo hast Du gestockt?', value: whatConfused, set: setWhatConfused, ph: 'Etwas, das Dich verwirrt oder aufgehalten hat …' },
    { label: 'Was wünschst Du Dir?', value: wishes, set: setWishes, ph: 'Etwas, das CarmaCircle für Dich noch braucht …' },
    { label: 'Alles andere', value: generalFeedback, set: setGeneralFeedback, ph: 'Was Du uns sonst mitgeben möchtest …' },
  ];

  return (
    <div className="relative min-h-screen overflow-x-hidden text-[var(--text)]" data-testid="beta-guide-page">
      <div className="mask-bg-word mask-bg-top" aria-hidden="true">
        <span style={{ transform: 'translateX(-6%)' }}>GUIDE</span>
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto px-6 sm:px-8 pt-20 pb-28">
        {/* Einstieg */}
        <header className="mb-20">
          <p className="mask-enter text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-5">Guide</p>
          <h1 className="mask-enter mask-enter-delay-1 mask-headline text-3xl sm:text-4xl text-[var(--text)] mb-6">
            So funktioniert CarmaCircle.
          </h1>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed max-w-lg">
            CarmaCircle ist ein Werkzeug für echte Wertschätzung zwischen Menschen — kein soziales Netzwerk.
            Dieser Guide zeigt Dir die wichtigsten Wege. Nimm Dir Zeit. Es gibt kein richtig oder falsch.
          </p>
        </header>

        {/* Testwege */}
        <div className="space-y-16 mb-24">
          {SECTIONS.map((s) => (
            <section key={s.num} className="flex items-start gap-6" data-testid={`beta-guide-section-${s.num}`}>
              <span className="text-xs tracking-[0.2em] text-[var(--muted)] pt-2 shrink-0">{s.num}</span>
              <div>
                <h2 className="mask-headline text-xl sm:text-2xl text-[var(--text)] mb-2.5">{s.title}</h2>
                <p className="text-sm text-[var(--muted)] leading-relaxed mb-4 max-w-md">{s.intro}</p>
                <ul className="space-y-2.5">
                  {s.points.map((p) => (
                    <li key={p} className="text-sm text-[var(--text)]/70 leading-relaxed max-w-md pl-4 relative">
                      <span className="absolute left-0 top-[0.55em] w-1 h-1 rounded-full bg-[var(--text)]/40" aria-hidden="true" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ))}
        </div>

        {/* Leitfrage */}
        <section className="mb-24" data-testid="beta-guide-question">
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-5">Die wichtigste Frage</p>
          <p className="mask-headline text-2xl sm:text-3xl text-[var(--text)]/90 leading-relaxed max-w-lg">
            Fühlt sich alles wie dieselbe ruhige Welt an — oder wirkt etwas noch wie eine klassische App?
          </p>
        </section>

        {/* Feedback */}
        <section className="pt-10 border-t border-[var(--border)]/40" data-testid="beta-guide-feedback">
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-5">Dein Feedback</p>
          <p className="text-sm text-[var(--muted)] leading-relaxed mb-10 max-w-md">
            Alles, was Du hier teilst, lesen wir persönlich. Zwischendurch erreichst Du uns jederzeit über
            „Feedback geben“ unten rechts.
          </p>

          {!user ? (
            <div>
              <p className="text-sm text-[var(--muted)] mb-6">Bitte melde Dich an, um Feedback zu senden.</p>
              <SecondaryAction onClick={() => navigate('/auth')} data-testid="beta-guide-login-btn">Anmelden</SecondaryAction>
            </div>
          ) : submitted ? (
            <div className="mask-enter" data-testid="beta-guide-thanks">
              <p className="mask-headline text-xl text-[var(--text)] mb-2">Danke Dir.</p>
              <p className="text-sm text-[var(--muted)] mb-6">Deine Worte helfen uns, CarmaCircle Schritt für Schritt ruhiger und klarer zu machen.</p>
              <SecondaryAction onClick={() => setSubmitted(false)} data-testid="beta-guide-more-feedback">Weiteres Feedback geben</SecondaryAction>
            </div>
          ) : (
            <form onSubmit={submitFeedback} className="space-y-10">
              {fields.map((f) => (
                <div key={f.label}>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[var(--muted)] mb-3">{f.label}</label>
                  <textarea
                    value={f.value}
                    onChange={(e) => f.set(e.target.value)}
                    rows={2}
                    placeholder={f.ph}
                    className="mask-textarea text-base text-[var(--text)] min-h-[3.5rem]"
                  />
                </div>
              ))}
              {error && <p className="text-sm text-red-700/80" data-testid="beta-guide-error">{error}</p>}
              <PrimaryAction type="submit" disabled={submitting || !hasContent} data-testid="beta-guide-submit">
                {submitting ? 'Einen Moment …' : 'Feedback senden'}
              </PrimaryAction>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
